import { useEffect, useRef, useState } from "react";
import { Mic, Play, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import ThemedSelect from "./ThemedSelect";

/**
 * Iter-27.43 — Sélecteur de voix TTS PAR module (override l'admin global).
 *
 * Usage : embarqué dans l'éditeur de module pour permettre à l'admin de :
 *  - laisser la voix globale (default → null/empty)
 *  - choisir une voix spécifique pour ce module (avec preview)
 *  - ajuster la vitesse (slider 0.7 → 1.2)
 *
 * Props :
 *  - voiceId        : string (ou "" pour "hériter de la voix globale")
 *  - speed          : float 0.7..1.2
 *  - onChangeVoice  : (newVoiceId) => void
 *  - onChangeSpeed  : (newSpeed) => void
 */
export default function ModuleVoicePicker({ voiceId, speed, onChangeVoice, onChangeSpeed }) {
    const [voices, setVoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalVoiceId, setGlobalVoiceId] = useState("");
    const [previewing, setPreviewing] = useState(null);
    const [playing, setPlaying] = useState(null);
    const audioRef = useRef(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/admin/tts/voices");
                setVoices(data.voices || []);
                setGlobalVoiceId(data.active_voice_id || "");
            } catch {
                /* silencieux : l'admin global gère déjà l'erreur de toute façon */
            } finally {
                setLoading(false);
            }
        })();
        return () => {
            if (audioRef.current) audioRef.current.pause();
        };
    }, []);

    const handlePreview = async (vid) => {
        if (playing === vid && audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
            setPlaying(null);
            return;
        }
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
            setPlaying(null);
        }
        setPreviewing(vid);
        try {
            const form = new FormData();
            form.append("voice_id", vid);
            form.append("speed", String(speed || 1.1));
            const res = await api.post("/admin/tts/preview", form, {
                responseType: "blob",
                headers: { "Content-Type": "multipart/form-data" },
            });
            const audio = new Audio(URL.createObjectURL(res.data));
            audio.onended = () => setPlaying(null);
            audioRef.current = audio;
            await audio.play();
            setPlaying(vid);
        } catch {
            toast.error("Sample impossible");
        } finally {
            setPreviewing(null);
        }
    };

    const effective = voiceId || globalVoiceId;
    const effectiveName = voices.find((v) => v.voice_id === effective)?.name || "—";

    return (
        <div
            className="bg-white/3 border border-white/10 rounded-2xl p-4 space-y-3"
            data-testid="module-voice-picker"
        >
            <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                    <Mic className="w-4 h-4 text-shariti-orange-light" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-thin tracking-[0.22em] uppercase text-shariti-orange-light">
                        Voix du formateur · ce module
                    </p>
                    <p className="text-xs text-white/55 font-light leading-snug">
                        Override la voix admin globale uniquement pour les leçons de ce module.
                    </p>
                </div>
            </div>

            {/* Voice select */}
            <div>
                <label className="text-[10px] font-thin tracking-[0.18em] uppercase text-white/45 mb-1 block">
                    Voix utilisée pour ce module
                </label>
                {/* Iter-27.61 — Migration <select> natif vers ThemedSelect
                    pour design Shariti dark cohérent (au lieu du dropdown OS blanc). */}
                <ThemedSelect
                    value={voiceId || ""}
                    onChange={(v) => onChangeVoice(v)}
                    placeholder={`Hériter de la voix admin globale (${voices.find((v) => v.voice_id === globalVoiceId)?.name || "défaut"})`}
                    dataTestid="module-voice-select"
                    disabled={loading}
                    options={[
                        {
                            value: "",
                            label: `— Hériter de la voix admin globale (${voices.find((v) => v.voice_id === globalVoiceId)?.name || "défaut"}) —`,
                        },
                        ...voices.map((v) => ({
                            value: v.voice_id,
                            label: `${v.is_custom ? "✦ " : ""}${v.name}${v.gender ? ` · ${v.gender}` : ""}${v.descriptive ? ` · ${v.descriptive}` : ""}`,
                        })),
                    ]}
                />
                <div className="flex items-center gap-2 mt-2">
                    <button
                        type="button"
                        onClick={() => handlePreview(effective)}
                        disabled={!effective || previewing === effective}
                        data-testid="module-voice-preview"
                        className="text-[11px] font-bold inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/12 text-white/75 hover:text-white px-3 py-1.5 rounded-full transition-all disabled:opacity-40"
                    >
                        {previewing === effective ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : playing === effective ? (
                            <>
                                <Pause className="w-3.5 h-3.5" /> Stop
                            </>
                        ) : (
                            <>
                                <Play className="w-3.5 h-3.5" /> Écouter "{effectiveName}"
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Speed slider */}
            <div>
                <div className="flex items-baseline justify-between mb-1">
                    <label className="text-[10px] font-thin tracking-[0.18em] uppercase text-white/45">
                        Vitesse
                    </label>
                    <span className="text-[11px] font-extrabold text-shariti-orange-light tabular-nums">
                        {Number(speed || 1.1).toFixed(2)}×
                    </span>
                </div>
                <input
                    type="range"
                    min="0.70"
                    max="1.20"
                    step="0.05"
                    value={speed || 1.1}
                    onChange={(e) => onChangeSpeed(parseFloat(e.target.value))}
                    data-testid="module-voice-speed"
                    className="w-full accent-shariti-orange"
                />
                <div className="flex justify-between text-[9px] text-white/30 font-thin tracking-wider mt-1">
                    <span>0.7× (lent)</span>
                    <span>1.0×</span>
                    <span>1.2× (rapide)</span>
                </div>
            </div>
        </div>
    );
}
